// ============================================================
// DISMISS REGISTRY — "I've seen this, stop showing it"
// ============================================================
// Single source of truth for "user dismissed thing X, don't
// show it again." Used by:
//   - ToastCenter        (synthetic alert stream, info popups)
//   - HelpTour           (per-page coachmarks)
//   - RuntimeBanners     (Demo / Evaluation tenant chip)
//   - InstallBanner      (PWA install prompt)
//   - Alerts.jsx         (acknowledgments + assignee picks)
//
// Storage strategy:
//   - localStorage: persistent across browser restarts (HelpTour,
//                   InstallBanner, alert acks — anything where a
//                   user expects "I clicked dismiss, stop bothering
//                   me forever").
//   - sessionStorage: per-tab (Demo banner, toast dedupe — things
//                     a fresh session should re-evaluate).
//
// Keys are namespaced so we can wipe one bucket without nuking
// others (e.g. ops support flow: "clear all dismissed tours but
// keep alert acks").
//
// Every public function silently no-ops on storage errors (private
// mode, quota exceeded) so we never crash the page over a banner.

const NS = 'netcom_connect';

function safeRead(store, key) {
  try {
    const raw = store.getItem(`${NS}.${key}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function safeWrite(store, key, value) {
  try { store.setItem(`${NS}.${key}`, JSON.stringify(value)); return true; }
  catch { return false; }
}

// ─── Persistent (across browser restarts) ─────────────────

/**
 * Mark a thing as "dismissed forever." A future render that asks
 * isDismissedPersistent(key) will return true.
 *
 * `key` should be stable + descriptive, e.g.
 *   `tour:/`             (tour shown on home page)
 *   `tour:/network@v2`   (versioned — add `@vN` when you change a tour
 *                         to re-trigger for previously-dismissed users)
 *   `banner:install-pwa`
 *   `alert:ack:a-2026-0429-12`  (alert ack — survives refresh)
 *   `alert:assign:a-2026-0429-12:noc-shift-a`
 */
export function dismissPersistent(key, value = true) {
  if (typeof window === 'undefined') return;
  const cur = safeRead(window.localStorage, 'dismissed') || {};
  cur[key] = value;
  safeWrite(window.localStorage, 'dismissed', cur);
}

export function isDismissedPersistent(key) {
  if (typeof window === 'undefined') return false;
  const cur = safeRead(window.localStorage, 'dismissed') || {};
  return cur[key] != null && cur[key] !== false;
}

export function getPersistentValue(key) {
  if (typeof window === 'undefined') return null;
  const cur = safeRead(window.localStorage, 'dismissed') || {};
  return cur[key] ?? null;
}

/**
 * Replay something — un-mark dismissal so the user can see it
 * again. Used by the HelpTour "Replay all tours" button.
 */
export function clearDismissedPersistent(keyPrefix) {
  if (typeof window === 'undefined') return;
  const cur = safeRead(window.localStorage, 'dismissed') || {};
  for (const k of Object.keys(cur)) {
    if (!keyPrefix || k.startsWith(keyPrefix)) delete cur[k];
  }
  safeWrite(window.localStorage, 'dismissed', cur);
}

// ─── Session-scoped (cleared when tab closes) ─────────────

/**
 * Mark a thing as dismissed for THIS browser session only.
 * Used for demo / evaluation chrome that should re-evaluate
 * on a brand-new tab.
 */
export function dismissSession(key) {
  if (typeof window === 'undefined') return;
  const cur = safeRead(window.sessionStorage, 'dismissed') || {};
  cur[key] = true;
  safeWrite(window.sessionStorage, 'dismissed', cur);
}

export function isDismissedSession(key) {
  if (typeof window === 'undefined') return false;
  const cur = safeRead(window.sessionStorage, 'dismissed') || {};
  return !!cur[key];
}

// ─── Toast dedupe (special case) ──────────────────────────
// The synthetic alert stream picks from a pool of 7 toasts every
// 45s. Without dedupe, the same toast can fire 4× in a 5-min
// demo. We track "recently fired" content keys + suppress
// anything the user explicitly dismissed for the session.

const RECENT_TOAST_WINDOW_MS = 5 * 60_000;  // 5 min — don't show the same toast twice in this window

export function rememberToastFired(dedupeKey) {
  if (!dedupeKey || typeof window === 'undefined') return;
  const cur = safeRead(window.sessionStorage, 'toast-recent') || {};
  cur[dedupeKey] = Date.now();
  safeWrite(window.sessionStorage, 'toast-recent', cur);
}

export function wasToastRecentlyFired(dedupeKey) {
  if (!dedupeKey || typeof window === 'undefined') return false;
  const cur = safeRead(window.sessionStorage, 'toast-recent') || {};
  const ts = cur[dedupeKey];
  if (!ts) return false;
  return (Date.now() - ts) < RECENT_TOAST_WINDOW_MS;
}

export function rememberToastDismissed(dedupeKey) {
  if (!dedupeKey || typeof window === 'undefined') return;
  const cur = safeRead(window.sessionStorage, 'toast-dismissed') || {};
  cur[dedupeKey] = Date.now();
  safeWrite(window.sessionStorage, 'toast-dismissed', cur);
}

export function wasToastDismissed(dedupeKey) {
  if (!dedupeKey || typeof window === 'undefined') return false;
  const cur = safeRead(window.sessionStorage, 'toast-dismissed') || {};
  return !!cur[dedupeKey];
}
